import React, { useEffect, useState } from 'react';

import { NetworkType } from '../../../../types';
import ProviderBroadcastListView from '../../BroadcastListSettings/ProviderBroadcastListView';
import { mobxInjectSelect } from '../../../utils';
import { PatientBroadcastForm, BroadcastListsView } from '.';

const ACTIONS = {
  CREATE: 'create',
  EDIT: 'edit',
  SEARCH: 'search',
  VIEW: 'view',
};

type BroadcastListsProps = {
  isAdmin: boolean;
  network?: NetworkType;
  providerNetworkCustomDirectories?: string[];
  refreshProviderNetworkCustomDirectories?: () => void;
  setCurrentAdminPageRefresher: (fn: () => void) => void;
  setAppResetter?: (fn: () => void) => void;
};

type BLTypes = 'normal' | 'ad' | 'scim';

type MobxProps = {
  logGainsightAnalytics: (p: { parentPathName: string; pathName: string }) => void;
};

const BroadcastLists: React.FC<BroadcastListsProps & MobxProps> = ({
  isAdmin,
  network,
  setAppResetter = () => {},
  setCurrentAdminPageRefresher,
  providerNetworkCustomDirectories = [],
  refreshProviderNetworkCustomDirectories,
  logGainsightAnalytics,
}) => {
  const [action, setAction] = useState(ACTIONS.SEARCH);
  const [id, setId] = useState<string>('');
  const [blType, setBLType] = useState<BLTypes>('normal');

  useEffect(() => {
    setAppResetter(searchBroadcastLists);
    return () => setAppResetter(() => {});
  }, [setAppResetter]);

  useEffect(() => {
    if (action === ACTIONS.SEARCH) setBLType('normal');
  }, [action]);

  const createBroadcastList = () => {
    let parentPathName = 'Broadcast Lists';
    let pathName = 'Create Provider Broadcast List';
    setBLType('normal');
    setId('');
    setAction(ACTIONS.CREATE);

    if (network !== 'provider') {
      parentPathName = 'Patient Settings';
      pathName = 'Create Patient Broadcast List';
    }

    logGainsightAnalytics({
      parentPathName,
      pathName,
    });
  };

  const editBroadcastList = (id: string, type: BLTypes = 'normal') => {
    setBLType(type);
    setId(id);
    setAction(ACTIONS.EDIT);
  };

  const searchBroadcastLists = () => {
    setId('');
    setAction(ACTIONS.SEARCH);
  };

  const setMessageTemplateAdminPageRefresher = (fn: () => void) => {
    setCurrentAdminPageRefresher(() => {
      searchBroadcastLists();
      fn();
    });
  };

  switch (action) {
    case ACTIONS.CREATE:
    case ACTIONS.EDIT:
      if (network === 'provider') {
        return (
          <ProviderBroadcastListView
            action={action}
            blType={blType}
            id={id}
            isAdmin={isAdmin}
            isPregen={id.includes('@')}
            setBLType={setBLType}
            viewLists={searchBroadcastLists}
            customDirectories={providerNetworkCustomDirectories}
          />
        );
      }
      return (
        <PatientBroadcastForm
          id={id}
          isAdmin={isAdmin}
          searchBroadcastLists={searchBroadcastLists}
        />
      );
    case ACTIONS.SEARCH:
      return (
        <BroadcastListsView
          createBroadcastList={createBroadcastList}
          editBroadcastList={editBroadcastList}
          isAdmin={isAdmin}
          setBLType={setBLType}
          setMessageTemplateAdminPageRefresher={setMessageTemplateAdminPageRefresher}
          refreshProviderNetworkCustomDirectories={refreshProviderNetworkCustomDirectories}
        />
      );
    case ACTIONS.VIEW:
      return (
        <div>
          Not yet implemented {action} {id}
        </div>
      );
    default:
      return <></>;
  }
};

export default mobxInjectSelect<BroadcastListsProps, MobxProps>({
  trackerStore: ['logGainsightAnalytics'],
})(BroadcastLists);
